import React, {useEffect, useRef, useState} from "react";
import {Col, Form, Row} from "react-bootstrap";
import {useForm} from "react-hook-form";
import {AjaxComplete} from "../ajax-complete/AjaxComplete";
import {Jpostcode} from "jpostcode/web";
import {I18n} from "../../i18n";

const MSG_COUNTRY_SELECT = "国を候補から選択してください。";
const MSG_ZIP_PRESENT = "郵便番号を入力してください。";
const MSG_ZIP_FORMAT = "郵便番号を7桁で入力してください。"
const MSG_PREFECTURE_PRESENT = "都道府県を選択してください。";
const MSG_CITY_PRESENT = "市区郡町村を入力してください。";
const MSG_ADDRESS_PRESENT = "町名・番地を入力してください。";

const PREFECTURES = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県', '静岡県', '愛知県',
    '三重県', '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
    '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県',
    '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

export interface Address {
    country: any;
    zip: string;
    prefecture: string;
    city: string;
    address: string;
    building_name: string;
}
export const newEmptyAddress: () => Address =
    () => ({country: {code: 'jp', name: '日本'}, zip: '', prefecture: '', city: '', address: '', building_name: ''});

const FIELD_NAMES = ['zip', 'prefecture', 'city', 'address', 'building_name'] as const;

// 入力値は書き換えず(入力中にカーソルを奪わないため)、補完APIの検索時のみ半角化する。
// 半角への正規化自体はサーバーサイドが保存時に必ず行う
const toZipDigits = (value: string) =>
    value.replace(/[０-９]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
         .replace(/[-－ー‐−ｰ〒\s]/g, '');

declare interface AddressComponentProps {
    address?: Address;
    onAddressChange?: (Address) => void;
    onValidityChange?: (valid: boolean) => void;
    required?: boolean;
}
export const AddressComponent = (props: AddressComponentProps) => {
    const initialAddress = newEmptyAddress();
    // mode: "all" = 変更時に加えてフォーカスが外れたときも検証する。
    // 変更時のみだと、一度も触っていないフィールド(特に都道府県セレクト)のエラーが表示されない
    const { register, watch, setValue, getValues, trigger, formState: { errors, isValid } } = useForm<Address>({
      mode: "all",
      criteriaMode: "all",
      defaultValues: initialAddress,
    });

    const [country, setCountry] = useState(props.address?.country || initialAddress.country);
    const isJapan = (country?.code || '').toLowerCase() === 'jp';

    const prefectureInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
    const addressInputRef = useRef<HTMLInputElement | null>(null);

    // 自動入力の結果を差し込んだら番地入力へフォーカスを移す。見つからなければ都道府県へ
    const handleZipChange = async (e) => {
        if (!isJapan) return;
        const zipDigits = toZipDigits(e.target.value);
        if (!/^\d{7}$/.test(zipDigits)) return;

        // jpostcode は上3桁ごとのデータを CDN から取る。取得失敗（reject）も見つからない場合と同じ扱いにする。
        // 同じ郵便番号に複数の町域がある場合は先頭を使う（番地入力で直してもらう）
        const [found] = await Jpostcode.find(zipDigits).catch(() => []);
        if (!found) {
            prefectureInputRef.current?.focus();
            return;
        }
        setValue('prefecture', found.prefecture, {shouldValidate: true, shouldDirty: true});
        setValue('city', found.city, {shouldValidate: true, shouldDirty: true});
        // 事業所個別郵便番号なら番地（street）まで入る
        setValue('address', `${found.town}${found.street ?? ''}`, {shouldValidate: true, shouldDirty: true});
        if (found.officeName && !getValues('building_name')) {
            setValue('building_name', found.officeName, {shouldValidate: true, shouldDirty: true});
        }
        requestAnimationFrame(() => {
            const input = addressInputRef.current;
            if (!input) return;
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        });
    };

    const [addressCountryValidity, setAddressCountryValidity] = useState(true)

    const zip = watch('zip');
    const prefecture = watch('prefecture');
    const city = watch('city');
    const address = watch('address');
    const building_name = watch('building_name');

    useEffect(() => {
        props.onAddressChange?.({country, zip, prefecture, city, address, building_name});
    }, [country, zip, prefecture, city, address, building_name]);

    useEffect(() => {
        props.onValidityChange?.(isValid && addressCountryValidity);
    }, [isValid, addressCountryValidity]);

    useEffect(() => {
        if (!props.address) return;

        FIELD_NAMES.forEach((name) => {
            const value = props.address[name] ?? '';
            if (getValues(name) !== value) setValue(name, value, { shouldValidate: true });
        });
        if (props.address.country && props.address.country.code !== country.code) {
            setCountry(props.address.country)
        }
    }, [props.address])

    // 国によって必須条件・郵便番号の形式が変わるため再検証する。
    // マウント時に走ると未入力の全フィールドにエラーが出るため、国が実際に変わったときだけ発火する
    const prevCountryCode = useRef(country?.code);
    useEffect(() => {
        if (prevCountryCode.current === country?.code) return;
        prevCountryCode.current = country?.code;
        trigger();
    }, [country]);

    const {ref: prefectureFormRef, ...prefectureRegister} = register('prefecture', {
        validate: {
            required: value => !isJapan || value !== '' || MSG_PREFECTURE_PRESENT,
        }
    });
    const setPrefectureRef = (el) => {
        prefectureFormRef(el);
        prefectureInputRef.current = el;
    };
    const {ref: addressFormRef, ...addressRegister} = register('address', {
        required: MSG_ADDRESS_PRESENT,
        // 都道府県セレクトは触らないまま進めてしまえるため、番地・市区郡町村の検証時に一緒に検証する
        deps: ['prefecture'],
    });

    return <div>
        <div className="form-label">{I18n.translate('views.settings.shipping_address')}</div>
        <Form.Group>
            <Row>
                <Col>
                    <AjaxComplete url="/api/countries" delay={300}
                                  defaultValue={country}
                                  component={Form.Control}
                                  onValidityChange={setAddressCountryValidity}
                                  candidate={({candidate}) => <span>{candidate.name}</span>}
                                  onSelected={setCountry}
                                  placeholder={I18n.translate('views.settings.country')}
                                  required={props.required}
                    />
                </Col>
                <Col>
                    <Form.Control type="text" placeholder={I18n.translate('views.settings.zip')}
                                  required={props.required}
                                  {...register('zip', {
                                    required: MSG_ZIP_PRESENT,
                                    onChange: handleZipChange,
                                    validate: {
                                      format: value => !isJapan ||
                                      /^\d{7}$/.test(toZipDigits(value)) ||
                                      MSG_ZIP_FORMAT,
                                    }
                                  })}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col>
                    {isJapan
                        ? <Form.Control as="select" required={props.required}
                                        {...prefectureRegister} ref={setPrefectureRef}>
                            <option value="">{I18n.translate('views.settings.prefecture')}</option>
                            {PREFECTURES.map(pref => <option key={pref} value={pref}>{pref}</option>)}
                          </Form.Control>
                        : <Form.Control type="text" placeholder={I18n.translate('views.settings.prefecture')}
                                        {...prefectureRegister} ref={setPrefectureRef}
                          />}
                </Col>
                <Col>
                    <Form.Control type="text" placeholder={I18n.translate('views.settings.city')}
                                  required={props.required}
                                  {...register('city', {
                                    required: MSG_CITY_PRESENT,
                                    deps: ['prefecture'],
                                  })}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col>
                    <Form.Control type="text" placeholder={I18n.translate('views.settings.address')}
                                  required={props.required}
                                  {...addressRegister}
                                  ref={(el) => {
                                      addressFormRef(el);
                                      addressInputRef.current = el;
                                  }}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col>
                    <Form.Control type="text" placeholder={I18n.translate('views.settings.building_name')}
                                  {...register('building_name')}
                    />
                </Col>
            </Row>

            <Row className="mt-2">
              <Col>{[
                !addressCountryValidity && MSG_COUNTRY_SELECT,
                errors.zip?.types?.required,
                errors.zip?.types?.format,
                errors.prefecture?.types?.required,
                errors.city?.types?.required,
                errors.address?.types?.required,
                errors.building_name?.types?.required,
                ].filter(Boolean).map((msg, i) => <li key={i} className="text-danger">{msg}</li>)}
              </Col>
            </Row>
      </Form.Group>
    </div>;
};
